import { ApiError } from './ApiError';
import { isApiErrorCode } from './types';
import type {
  HealthResponse,
  QueueUpdate,
  RecommendRequest,
  RecommendResponse,
  Source,
  WrappedResponse,
} from './types';

/**
 * CONTRACT.md §4: `VITE_API_BASE`, default `/api`. In production nginx proxies
 * that prefix to the backend, so the default is correct for the container and
 * for `npm run dev` (see the Vite dev proxy).
 */
export const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

/** CONTRACT.md §1 — request field bounds. Enforced here so bad input never leaves the browser. */
export const USERNAME_MAX_LENGTH = 64;

/** The backend's public sample rider (backend/app/demo.py), for anyone without a list. */
export const DEMO_RIDER = 'animetro-demo';
export const DEFAULT_LIMIT = 10;

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

/**
 * Turn a non-2xx response into an ApiError. The body is *supposed* to be the
 * documented envelope, but a 502 can just as easily be an nginx HTML page, so
 * every step of the unwrap is defensive and falls back to a code derived from
 * the status rather than showing the user the status itself.
 */
async function errorFromResponse(response: Response): Promise<ApiError> {
  const retryAfterSeconds = parseRetryAfter(response.headers.get('Retry-After'));

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  const envelope =
    payload !== null && typeof payload === 'object' && 'error' in payload
      ? (payload as { error: unknown }).error
      : null;

  if (envelope !== null && typeof envelope === 'object') {
    const { code, message } = envelope as { code?: unknown; message?: unknown };
    if (typeof code === 'string' && isApiErrorCode(code)) {
      return new ApiError(code, {
        serverMessage: typeof message === 'string' ? message : null,
        retryAfterSeconds,
      });
    }
  }

  // Well-formed status, unrecognised body: still map the statuses the contract
  // documents, because the status alone tells us what happened.
  if (response.status === 429) {
    return new ApiError('rate_limited', { retryAfterSeconds });
  }
  if (response.status === 503) {
    return new ApiError('model_unavailable');
  }
  if (response.status >= 500) {
    return new ApiError('upstream_error');
  }
  return new ApiError('unknown_error');
}

/** Minimal structural check — a 200 with the wrong shape is a bug we want to see as an error, not a crash mid-render. */
function isRecommendResponse(value: unknown): value is RecommendResponse {
  if (value === null || typeof value !== 'object') return false;
  const candidate = value as Partial<RecommendResponse>;
  return (
    typeof candidate.profile === 'object' &&
    candidate.profile !== null &&
    Array.isArray(candidate.recommendations)
  );
}

export async function fetchRecommendations(
  request: RecommendRequest,
  signal?: AbortSignal,
): Promise<RecommendResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/recommend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    });
  } catch (cause) {
    // An aborted request is a caller decision, not a failure to report.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('network_error');
  }

  if (!response.ok) {
    throw await errorFromResponse(response);
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError('unknown_error');
  }

  if (!isRecommendResponse(body)) {
    throw new ApiError('unknown_error');
  }

  return body;
}

/** One server-sent event. */
export interface SseEvent {
  event: string;
  data: string;
}

/** Split complete events off the front of a buffer; `rest` waits for more bytes. */
export function parseSse(buffer: string): { events: SseEvent[]; rest: string } {
  const frames = buffer.split('\n\n');
  const rest = frames.pop() ?? '';
  const events: SseEvent[] = [];
  for (const frame of frames) {
    let event = 'message';
    const data: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event: ')) event = line.slice(7);
      else if (line.startsWith('data: ')) data.push(line.slice(6));
    }
    if (data.length > 0) events.push({ event, data: data.join('\n') });
  }
  return { events, rest };
}

function errorFromEvent(data: string): ApiError {
  let body: { error?: { code?: unknown; message?: unknown }; status?: unknown; retry_after?: unknown };
  try {
    body = JSON.parse(data);
  } catch {
    return new ApiError('unknown_error');
  }
  const code = body.error?.code;
  const retryAfterSeconds = typeof body.retry_after === 'number' ? body.retry_after : null;
  if (typeof code === 'string' && isApiErrorCode(code)) {
    return new ApiError(code, {
      serverMessage: typeof body.error?.message === 'string' ? body.error.message : null,
      retryAfterSeconds,
    });
  }
  if (typeof body.status === 'number' && body.status >= 500) return new ApiError('upstream_error');
  return new ApiError('unknown_error');
}

/**
 * /recommend with a running commentary on the queue. Anything that stops the
 * stream working (a proxy in the way, an older backend, a connection dropped
 * half way) falls back to the plain route: the answer matters more than the
 * commentary, and the backend's caches mean asking again costs next to nothing.
 */
export async function fetchRecommendationsLive(
  request: RecommendRequest,
  onQueue: (update: QueueUpdate) => void,
  signal?: AbortSignal,
): Promise<RecommendResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/recommend/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('network_error');
  }

  if (response.status === 404 || response.status === 405) {
    return fetchRecommendations(request, signal);
  }
  if (!response.ok) throw await errorFromResponse(response);

  const isStream = response.headers.get('Content-Type')?.includes('text/event-stream');
  if (!isStream || !response.body) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new ApiError('unknown_error');
    }
    if (!isRecommendResponse(body)) throw new ApiError('unknown_error');
    return body;
  }

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      const parsed = parseSse(buffer + value);
      buffer = parsed.rest;
      for (const { event, data } of parsed.events) {
        if (event === 'queue') {
          onQueue(JSON.parse(data) as QueueUpdate);
        } else if (event === 'error') {
          throw errorFromEvent(data);
        } else if (event === 'result') {
          const body: unknown = JSON.parse(data);
          if (!isRecommendResponse(body)) throw new ApiError('unknown_error');
          return body;
        }
      }
    }
  } catch (cause) {
    if (cause instanceof ApiError) throw cause;
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    // Dropped half way, or a frame that would not parse: try the plain route.
  } finally {
    reader.cancel().catch(() => undefined);
  }
  return fetchRecommendations(request, signal);
}

export async function fetchHealth(signal?: AbortSignal): Promise<HealthResponse> {
  const response = await fetch(`${API_BASE}/health`, { signal }).catch(() => {
    throw new ApiError('network_error');
  });
  if (!response.ok) throw await errorFromResponse(response);
  return (await response.json()) as HealthResponse;
}

/**
 * The intro's statistics. A 200 with the wrong shape is an error here for the
 * same reason as above, and the caller treats any failure as "no intro".
 */
export async function fetchWrapped(
  request: { username: string; source: Source; patient?: boolean },
  signal?: AbortSignal,
): Promise<WrappedResponse> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/wrapped`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError('network_error');
  }
  if (!response.ok) throw await errorFromResponse(response);

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new ApiError('unknown_error');
  }
  const candidate = body as Partial<WrappedResponse> | null;
  if (!candidate || typeof candidate !== 'object' || typeof candidate.departure !== 'object') {
    throw new ApiError('unknown_error');
  }
  return candidate as WrappedResponse;
}
